import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Shield, Refresh } from '@mui/icons-material';
import {
  policiesAPI,
  claimsAPI,
  dashboardAPI,
  farmsAPI,
  pondsAPI,
} from 'services/api';

// Section components
import RiskKPICards from './components/RiskKPICards';
import RiskFilters from './components/RiskFilters';
import RiskHeatmapSection from './components/RiskHeatmapSection';
import SpeciesRiskSection from './components/SpeciesRiskSection';
import ClaimsTrendSection from './components/ClaimsTrendSection';
import PolicyAgingSection from './components/PolicyAgingSection';
import IndemnityForecastSection from './components/IndemnityForecastSection';

/* ── Helper: month short name ───────────────────────────── */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (d) => `${MONTHS[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`;

/* ── Default filter state ───────────────────────────────── */
const DEFAULT_FILTERS = { plan: 'ALL', species: 'ALL', district: 'ALL', docRange: [0, 180] };

/**
 * InsuranceRiskDashboard — Admin Insurance Risk Engine.
 *
 * Data is sourced entirely from existing APIs:
 *   - policiesAPI.getAll()
 *   - claimsAPI.getAll()
 *   - dashboardAPI.getAdminDashboard()
 *   - farmsAPI.getAll()
 *   - pondsAPI (via farms)
 *
 * No backend schema changes.
 */
const InsuranceRiskDashboard = () => {
  // ── State ──────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Raw data
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  const [adminStats, setAdminStats] = useState({});
  const [farms, setFarms] = useState([]);
  const [ponds, setPonds] = useState([]);

  // ── Data Loading ───────────────────────────────────────
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        policiesAPI.getAll(),
        claimsAPI.getAll(),
        dashboardAPI.getAdminDashboard(),
        farmsAPI.getAll(),
      ]);

      const pol = results[0].status === 'fulfilled' ? (results[0].value.data || []) : [];
      const clm = results[1].status === 'fulfilled' ? (results[1].value.data || []) : [];
      const stats = results[2].status === 'fulfilled' ? (results[2].value.data || {}) : {};
      const frm = results[3].status === 'fulfilled' ? (results[3].value.data || []) : [];

      setPolicies(pol);
      setClaims(clm);
      setAdminStats(stats);
      setFarms(frm);

      // Fetch ponds from farms
      const allPonds = [];
      for (const farm of frm) {
        try {
          const res = await pondsAPI.getByFarm(farm.id);
          allPonds.push(...(res.data || []));
        } catch {
          // Silently skip farms we can't fetch ponds for
        }
      }
      setPonds(allPonds);
    } catch (err) {
      console.error('Risk dashboard load error:', err);
      setError('Failed to load insurance risk data. Some data may be unavailable.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived: Pond + Farm lookup maps ───────────────────
  const pondMap = useMemo(() => {
    const m = {};
    ponds.forEach((p) => { m[p.id] = p; });
    return m;
  }, [ponds]);

  const farmMap = useMemo(() => {
    const m = {};
    farms.forEach((f) => { m[f.id] = f; });
    return m;
  }, [farms]);

  // ── District list (dynamic) ────────────────────────────
  const allDistricts = useMemo(() => {
    const set = new Set();
    farms.forEach((f) => { if (f.district) set.add(f.district); });
    return [...set].sort();
  }, [farms]);

  // ── Apply filters ──────────────────────────────────────
  const enrichedPolicies = useMemo(() => {
    return policies.map((p) => {
      const pond = pondMap[p.pond_id] || {};
      const farm = farmMap[pond.farm_id] || {};
      return { ...p, _pond: pond, _farm: farm };
    });
  }, [policies, pondMap, farmMap]);

  const filtered = useMemo(() => {
    return enrichedPolicies.filter((p) => {
      // Plan
      if (filters.plan !== 'ALL') {
        const planVal = typeof p.plan === 'string' ? p.plan : p.plan?.value || '';
        if (planVal.toUpperCase() !== filters.plan) return false;
      }
      // Species
      if (filters.species !== 'ALL') {
        const sp = p._pond.species || '';
        const spVal = typeof sp === 'string' ? sp : sp?.value || '';
        if (spVal !== filters.species) return false;
      }
      // District
      if (filters.district !== 'ALL') {
        if ((p._farm.district || '') !== filters.district) return false;
      }
      // DOC range
      const doc = p._pond.doc_requested || 0;
      if (doc < filters.docRange[0] || doc > filters.docRange[1]) return false;

      return true;
    });
  }, [enrichedPolicies, filters]);

  // ── Filtered claims ────────────────────────────────────
  const filteredPolicyIds = useMemo(() => new Set(filtered.map((p) => p.id)), [filtered]);

  const filteredClaims = useMemo(() => {
    return claims.filter((c) => filteredPolicyIds.has(c.policy_id));
  }, [claims, filteredPolicyIds]);

  // ── KPI data ───────────────────────────────────────────
  const kpiData = useMemo(() => {
    const statusLower = (s) => (typeof s === 'string' ? s : s?.value || '').toLowerCase();
    const activePols = filtered.filter((p) => statusLower(p.status) === 'active');
    const totalPremium = adminStats.total_premium || 0;
    const totalClaimAmount = filteredClaims.reduce((s, c) => s + (c.estimated_loss_amount || 0), 0);

    // District exposure
    const districtExp = {};
    filtered.forEach((p) => {
      const d = p._farm.district || 'Unknown';
      districtExp[d] = (districtExp[d] || 0) + (p.sum_insured?.si_amount || 0);
    });
    const topD = Object.entries(districtExp).sort((a, b) => b[1] - a[1])[0];

    // Disease frequency (approximate from claim reasons)
    const diseaseKeywords = ['disease', 'wssv', 'ems', 'white spot', 'vibrio', 'infection', 'mortality'];
    const diseaseClaims = filteredClaims.filter((c) => {
      const reason = (c.reason || '').toLowerCase();
      return diseaseKeywords.some((kw) => reason.includes(kw));
    });
    const diseaseReasons = {};
    diseaseClaims.forEach((c) => {
      const r = (c.reason || 'Disease').split(' ').slice(0, 3).join(' ');
      diseaseReasons[r] = (diseaseReasons[r] || 0) + 1;
    });
    const topDisease = Object.entries(diseaseReasons).sort((a, b) => b[1] - a[1])[0];

    return {
      activePolicies: activePols.length,
      totalPolicies: filtered.length,
      totalClaims: filteredClaims.length,
      totalPremium,
      totalClaimAmount,
      distinctDistricts: Object.keys(districtExp).length,
      topDistrict: topD ? topD[0] : 'N/A',
      topDistrictExposure: topD ? topD[1] : 0,
      diseaseClaimCount: diseaseClaims.length,
      topDisease: topDisease ? topDisease[0] : 'N/A',
      newPoliciesThisMonth: adminStats.recent_policies || 0,
    };
  }, [filtered, filteredClaims, adminStats]);

  // ── District heatmap data ──────────────────────────────
  const heatmapData = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      const d = p._farm.district || 'Unknown';
      if (!map[d]) map[d] = { district: d, exposure: 0, policies: 0, claims: 0 };
      map[d].exposure += p.sum_insured?.si_amount || 0;
      map[d].policies += 1;
    });
    filteredClaims.forEach((c) => {
      const pol = enrichedPolicies.find((p) => p.id === c.policy_id);
      const d = pol?._farm?.district || 'Unknown';
      if (map[d]) map[d].claims += 1;
    });
    return Object.values(map);
  }, [filtered, filteredClaims, enrichedPolicies]);

  // ── Species risk data ──────────────────────────────────
  const speciesData = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      const sp = p._pond.species || 'Unknown';
      const spVal = typeof sp === 'string' ? sp : sp?.value || 'Unknown';
      if (!map[spVal]) map[spVal] = { species: spVal, exposure: 0, policies: 0, claims: 0 };
      map[spVal].exposure += p.sum_insured?.si_amount || 0;
      map[spVal].policies += 1;
    });
    filteredClaims.forEach((c) => {
      const pol = enrichedPolicies.find((p) => p.id === c.policy_id);
      const sp = pol?._pond?.species || 'Unknown';
      const spVal = typeof sp === 'string' ? sp : sp?.value || 'Unknown';
      if (map[spVal]) map[spVal].claims += 1;
    });
    return Object.values(map).sort((a, b) => b.exposure - a.exposure);
  }, [filtered, filteredClaims, enrichedPolicies]);

  // ── Claims trend (monthly) ─────────────────────────────
  const claimsTrendData = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ date: d, month: monthLabel(d), filed: 0, approved: 0, rejected: 0 });
    }
    filteredClaims.forEach((c) => {
      const cd = new Date(c.claim_date || c.created_at);
      const bucket = months.find(
        (m) => m.date.getFullYear() === cd.getFullYear() && m.date.getMonth() === cd.getMonth()
      );
      if (bucket) {
        bucket.filed += 1;
        const st = (typeof c.status === 'string' ? c.status : c.status?.value || '').toLowerCase();
        if (st === 'approved' || st === 'paid') bucket.approved += 1;
        if (st === 'rejected') bucket.rejected += 1;
      }
    });
    return months;
  }, [filteredClaims]);

  // ── Policy aging (DOC buckets) ─────────────────────────
  const policyAgingData = useMemo(() => {
    const buckets = [
      { bucket: '0-30', min: 0, max: 30, active: 0, claimed: 0, expired: 0 },
      { bucket: '31-60', min: 31, max: 60, active: 0, claimed: 0, expired: 0 },
      { bucket: '61-90', min: 61, max: 90, active: 0, claimed: 0, expired: 0 },
      { bucket: '91-120', min: 91, max: 120, active: 0, claimed: 0, expired: 0 },
      { bucket: '121-150', min: 121, max: 150, active: 0, claimed: 0, expired: 0 },
      { bucket: '150+', min: 151, max: 999, active: 0, claimed: 0, expired: 0 },
    ];
    filtered.forEach((p) => {
      const doc = p._pond.doc_requested || 0;
      const b = buckets.find((bk) => doc >= bk.min && doc <= bk.max);
      if (!b) return;
      const st = (typeof p.status === 'string' ? p.status : p.status?.value || '').toLowerCase();
      if (st === 'active') b.active += 1;
      else if (st === 'claimed') b.claimed += 1;
      else if (st === 'expired' || st === 'cancelled') b.expired += 1;
      else b.active += 1; // pending → active bucket
    });
    return buckets;
  }, [filtered]);

  // ── Indemnity forecast ─────────────────────────────────
  const indemnityData = useMemo(() => {
    const now = new Date();
    const months = [];

    // Historical (last 6 months)
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({ date: d, month: monthLabel(d), actual: 0, reserved: 0, forecast: null });
    }
    filteredClaims.forEach((c) => {
      const cd = new Date(c.claim_date || c.created_at);
      const bucket = months.find(
        (m) => m.date.getFullYear() === cd.getFullYear() && m.date.getMonth() === cd.getMonth()
      );
      if (!bucket) return;
      const st = (typeof c.status === 'string' ? c.status : c.status?.value || '').toLowerCase();
      if (st === 'approved' || st === 'paid') {
        bucket.actual += c.payout_amount || c.estimated_loss_amount || 0;
      } else if (st === 'pending') {
        bucket.reserved += c.estimated_loss_amount || 0;
      }
    });

    // Calculate average monthly payout for forecasting
    const historicalAvg =
      months.reduce((s, m) => s + m.actual, 0) / Math.max(months.filter((m) => m.actual > 0).length, 1);

    // Forecast (next 3 months)
    for (let i = 1; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      // Simple trend: avg * (1 + seasonality factor)
      const seasonFactor = [0.8, 0.9, 1.1, 1.2, 1.3, 1.4, 1.3, 1.2, 1.0, 0.9, 0.8, 0.7];
      const factor = seasonFactor[d.getMonth()] || 1;
      months.push({
        date: d,
        month: monthLabel(d),
        actual: 0,
        reserved: 0,
        forecast: Math.round(historicalAvg * factor),
      });
    }

    return months;
  }, [filteredClaims]);

  const indemnSummary = useMemo(() => {
    const totalPaid = indemnityData
      .filter((d) => d.actual > 0)
      .reduce((s, d) => s + d.actual, 0);
    const forecasted = indemnityData
      .filter((d) => d.forecast != null)
      .reduce((s, d) => s + d.forecast, 0);
    const claimCount = filteredClaims.filter((c) => {
      const st = (typeof c.status === 'string' ? c.status : c.status?.value || '').toLowerCase();
      return st === 'approved' || st === 'paid';
    }).length;

    return {
      totalPaidYTD: totalPaid,
      forecastNextQtr: forecasted,
      avgPayout: claimCount > 0 ? totalPaid / claimCount : 0,
      expectedLossRatio: adminStats.total_premium > 0
        ? ((totalPaid + forecasted) / adminStats.total_premium * 100)
        : 0,
    };
  }, [indemnityData, filteredClaims, adminStats]);

  // ── Render ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Shield sx={{ fontSize: 22, color: '#6366f1' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Insurance Risk Engine</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Comprehensive risk analytics for aquaculture insurance portfolio
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => loadData(true)}
            disabled={refreshing}
            sx={{ backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' }, mt: { xs: 2, sm: 0 } }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {/* Error */}
      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 3 }}
          action={<Button size="small" onClick={() => loadData(true)}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <section className="mb-5">
        <RiskKPICards data={kpiData} loading={loading} />
      </section>

      {/* Filters */}
      <section className="mb-5">
        <RiskFilters
          filters={filters}
          onChange={setFilters}
          districts={allDistricts}
          onReset={() => setFilters(DEFAULT_FILTERS)}
        />
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#6366f1' }} />
          <span className="ml-3 text-sm text-gray-500">Loading risk engine data...</span>
        </div>
      ) : (
        <>
          {/* Row 1: Heatmap + Species (side by side) */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <RiskHeatmapSection data={heatmapData} loading={false} />
            <SpeciesRiskSection data={speciesData} loading={false} />
          </section>

          {/* Row 2: Claims Trend (full width) */}
          <section className="mb-5">
            <ClaimsTrendSection data={claimsTrendData} loading={false} />
          </section>

          {/* Row 3: Policy Aging + Indemnity Forecast */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <PolicyAgingSection data={policyAgingData} loading={false} />
            <IndemnityForecastSection
              data={indemnityData}
              summary={indemnSummary}
              loading={false}
            />
          </section>
        </>
      )}
    </div>
  );
};

export default InsuranceRiskDashboard;
